/**
 * Word List Detail Page
 * View and manage individual word list
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { apiRequest } from '@/api/client';
import { useNotification } from '@/hooks/useNotification';

interface WordList {
  wordListId: string;
  name: string;
  className: string;
  classId: string;
  words: string[];
  isActive: boolean;
  createdAt: string;
}

export const WordListDetailPage = () => {
  const { wordListId } = useParams<{ wordListId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [wordList, setWordList] = useState<WordList | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSettingActive, setIsSettingActive] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (wordListId) {
      fetchWordList();
    }
  }, [wordListId]);

  const fetchWordList = async () => {
    try {
      setIsLoading(true);
      
      const response = await apiRequest<{ wordList: WordList }>('get_wordlist', {
        wordListId,
        teacherEmail: user?.email,
      });
      
      if (response.success && (response as any).wordList) {
        setWordList((response as any).wordList);
      }
    } catch (error) {
      console.error('Failed to fetch word list:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetActive = async () => {
    if (!wordList) return;
    
    try {
      setIsSettingActive(true);
      
      const response = await apiRequest('set_current_wordlist', {
        teacherEmail: user?.email,
        classId: wordList.classId,
        wordListId: wordList.wordListId,
      });

      if (response.success) {
        showNotification('success', 'Word list set as active');
        fetchWordList();
      } else {
        showNotification('error', 'Failed to set word list as active');
      }
    } catch (error) {
      console.error('Failed to set active:', error);
      showNotification('error', 'Failed to set word list as active');
    } finally {
      setIsSettingActive(false);
    }
  };

  const handleDeleteWordList = async () => {
    try {
      setIsDeleting(true);
      
      const response = await apiRequest('delete_wordlist', {
        listId: wordListId,
        userEmail: user?.email,
      });

      if (response.success) {
        showNotification('success', 'Word list deleted successfully');
        navigate('/wordlists');
      } else {
        showNotification('error', response.error || 'Failed to delete word list');
      }
    } catch (error) {
      console.error('Failed to delete word list:', error);
      showNotification('error', 'Failed to delete word list');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!wordList) {
    return (
      <div className="space-y-6">
        <Card>
          <div className="p-12 text-center">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Word list not found
            </h3>
            <p className="text-slate-600 mb-6">
              This word list may have been deleted or you don't have permission to view it.
            </p>
            <Button variant="primary" onClick={() => navigate('/wordlists')}>
              Back to Word Lists
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-slate-900">{wordList.name}</h1>
            <span className={`px-3 py-1 text-sm font-medium rounded-lg ${
              (wordList as any).listType === 'phonics' 
                ? 'bg-purple-100 text-purple-700' 
                : 'bg-green-100 text-green-700'
            }`}>
              {(wordList as any).listType === 'phonics' ? 'Sounds' : 'Words'}
            </span>
            {wordList.isActive && (
              <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-medium rounded-lg">
                Active
              </span>
            )}
          </div>
          <p className="text-slate-600 mt-2">{wordList.className}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/wordlists')}>
            ← Back to Word Lists
          </Button>
          <Button variant="primary" onClick={() => navigate(`/wordlists/${wordListId}/edit`)}>
            Edit
          </Button>
          {!wordList.isActive && (
            <Button
              variant="outline"
              onClick={handleSetActive}
              disabled={isSettingActive}
            >
              {isSettingActive ? 'Setting...' : 'Set as Active'}
            </Button>
          )}
          <Button variant="danger" onClick={() => setShowDeleteDialog(true)}>
            Delete
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteWordList}
        title="Delete Word List"
        message={`Are you sure you want to delete "${wordList.name}"? This will remove all ${wordList.words.length} words from this list. This action cannot be undone.`}
        confirmText="Delete Word List"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Word List Info */}
      <Card title="Word List Information">
        <div className="p-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-600">Total Words</label>
            <p className="text-lg text-slate-900">{wordList.words.length}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">Status</label>
            <p className="text-lg text-slate-900">
              {wordList.isActive ? 'Active - Students will see these words in their stories' : 'Inactive'}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">Created</label>
            <p className="text-lg text-slate-900">
              {new Date(wordList.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
      </Card>

      {/* Words */}
      <Card title="Words">
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {wordList.words.map((word, index) => (
              <div
                key={index}
                className="px-4 py-3 bg-slate-50 rounded-lg text-slate-900 font-medium"
              >
                {word}
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Actions */}
      <Card title="Actions">
        <div className="p-6 space-y-3">
          {!wordList.isActive && (
            <Button
              variant="primary"
              onClick={handleSetActive}
              disabled={isSettingActive}
              className="w-full"
            >
              {isSettingActive ? 'Setting as Active...' : 'Set as Active Word List'}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => {
              const text = wordList.words.join('\n');
              navigator.clipboard.writeText(text);
              showNotification('success', 'Words copied to clipboard');
            }}
            className="w-full"
          >
            📋 Copy All Words
          </Button>
        </div>
      </Card>
    </div>
  );
};
